import { useState } from "react";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import api from "@/lib/api";
import { toast } from "sonner";
import { useNavigate } from "react-router-dom";
import { cn } from "@/lib/utils";
import { BOARD_COLORS, gradientFor } from "@/lib/boardColors";
import { LayoutTemplate, Sparkles, ChevronLeft, Layers, ListChecks } from "lucide-react";

export default function CreateBoardDialog({ open, onOpenChange, defaultWorkspaceId }) {
  // mode: "choose" | "blank" | "templates" | "template-preview"
  const [mode, setMode] = useState("choose");
  const [selectedTemplateId, setSelectedTemplateId] = useState(null);

  const reset = () => {
    setMode("choose");
    setSelectedTemplateId(null);
  };

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) reset(); onOpenChange(o); }}>
      <DialogContent
        className="glass-panel border-white/40 dark:border-white/10 sm:max-w-3xl rounded-2xl max-h-[90vh] overflow-y-auto"
        data-testid="create-board-dialog"
      >
        {mode === "choose" && (
          <ChooseMode
            onPickBlank={() => setMode("blank")}
            onPickTemplates={() => setMode("templates")}
          />
        )}

        {mode === "blank" && (
          <BlankForm
            defaultWorkspaceId={defaultWorkspaceId}
            onBack={() => setMode("choose")}
            onClose={() => onOpenChange(false)}
          />
        )}

        {mode === "templates" && (
          <TemplatesGallery
            onBack={() => setMode("choose")}
            onPick={(id) => { setSelectedTemplateId(id); setMode("template-preview"); }}
          />
        )}

        {mode === "template-preview" && (
          <TemplatePreview
            templateId={selectedTemplateId}
            defaultWorkspaceId={defaultWorkspaceId}
            onBack={() => setMode("templates")}
            onClose={() => onOpenChange(false)}
          />
        )}
      </DialogContent>
    </Dialog>
  );
}

// ---------------------------------------------------------------------------
function ChooseMode({ onPickBlank, onPickTemplates }) {
  return (
    <>
      <DialogHeader>
        <DialogTitle className="font-display text-2xl">Crear nuevo board</DialogTitle>
        <DialogDescription>¿Cómo quieres empezar?</DialogDescription>
      </DialogHeader>
      <div className="grid sm:grid-cols-2 gap-4 pt-2" data-testid="create-board-choose-mode">
        <button
          type="button"
          onClick={onPickTemplates}
          data-testid="choose-templates"
          className="group relative overflow-hidden text-left rounded-2xl p-6 border border-blue-300/40 bg-gradient-to-br from-blue-50/80 to-blue-100/40 dark:from-blue-950/40 dark:to-slate-900/40 hover:shadow-[0_18px_40px_-15px_rgba(37,99,235,0.45)] transition-all"
        >
          <div className="inline-flex items-center justify-center h-10 w-10 rounded-xl bg-blue-600 text-white shadow-md mb-3">
            <LayoutTemplate className="h-5 w-5" />
          </div>
          <h3 className="font-display text-lg font-semibold mb-1">Usar plantilla</h3>
          <p className="text-sm text-muted-foreground">
            Empieza desde un tablero pre-cargado con listas y tareas listas para personalizar.
          </p>
          <div className="mt-3 inline-flex items-center text-xs font-medium text-blue-600 dark:text-blue-300 opacity-80 group-hover:opacity-100">
            Ver plantillas →
          </div>
        </button>

        <button
          type="button"
          onClick={onPickBlank}
          data-testid="choose-blank"
          className="group text-left rounded-2xl p-6 border border-white/40 dark:border-white/10 bg-white/40 dark:bg-slate-900/40 hover:shadow-[0_18px_40px_-15px_rgba(15,23,42,0.25)] transition-all"
        >
          <div className="inline-flex items-center justify-center h-10 w-10 rounded-xl bg-slate-800 dark:bg-white/10 text-white shadow-md mb-3">
            <Sparkles className="h-5 w-5" />
          </div>
          <h3 className="font-display text-lg font-semibold mb-1">Empezar desde cero</h3>
          <p className="text-sm text-muted-foreground">
            Crea un tablero en blanco y arma tu propio flujo de trabajo.
          </p>
          <div className="mt-3 inline-flex items-center text-xs font-medium text-slate-600 dark:text-slate-300 opacity-80 group-hover:opacity-100">
            Empezar en blanco →
          </div>
        </button>
      </div>
    </>
  );
}

// ---------------------------------------------------------------------------
function BlankForm({ defaultWorkspaceId, onBack, onClose }) {
  const navigate = useNavigate();
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [color, setColor] = useState("blue");
  const [workspaceId, setWorkspaceId] = useState(defaultWorkspaceId || "");
  const qc = useQueryClient();

  const { data: workspaces = [] } = useQuery({
    queryKey: ["workspaces"],
    queryFn: async () => (await api.get("/workspaces")).data,
  });

  const m = useMutation({
    mutationFn: async () => (
      await api.post(`/workspaces/${workspaceId || workspaces[0]?.workspace_id}/boards`,
        { name, description, cover_color: color })
    ).data,
    onSuccess: (board) => {
      qc.invalidateQueries({ queryKey: ["recent-boards"] });
      qc.invalidateQueries({ queryKey: ["boards", workspaceId] });
      toast.success("Board creado");
      onClose();
      navigate(`/boards/${board.board_id}`);
    },
    onError: () => toast.error("No se pudo crear el board"),
  });

  const submit = (e) => {
    e.preventDefault();
    if (!name.trim()) return toast.error("Debes ingresar un nombre");
    const wsid = workspaceId || workspaces[0]?.workspace_id;
    if (!wsid) return toast.error("Crea primero un workspace");
    setWorkspaceId(wsid);
    m.mutate();
  };

  return (
    <>
      <DialogHeader>
        <button onClick={onBack} className="inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground -mb-1 self-start" data-testid="blank-back">
          <ChevronLeft className="h-4 w-4" /> Atrás
        </button>
        <DialogTitle className="font-display">Nuevo board en blanco</DialogTitle>
        <DialogDescription>Empieza con un tablero vacío.</DialogDescription>
      </DialogHeader>
      <form onSubmit={submit} className="space-y-4">
        <div className={cn("h-24 rounded-xl bg-gradient-to-br shadow-lg", gradientFor(color))} />
        <div>
          <label className="text-xs font-medium text-muted-foreground">Workspace</label>
          <Select value={workspaceId} onValueChange={setWorkspaceId}>
            <SelectTrigger data-testid="create-board-workspace" className="mt-1 bg-white/60 dark:bg-slate-900/40">
              <SelectValue placeholder="Selecciona un workspace" />
            </SelectTrigger>
            <SelectContent>
              {workspaces.map((w) => (
                <SelectItem key={w.workspace_id} value={w.workspace_id}>{w.name}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div>
          <label className="text-xs font-medium text-muted-foreground">Nombre</label>
          <Input
            autoFocus value={name} onChange={(e) => setName(e.target.value)}
            placeholder="Lanzamiento Q1" data-testid="create-board-name"
            className="mt-1 bg-white/60 dark:bg-slate-900/40"
          />
        </div>
        <div>
          <label className="text-xs font-medium text-muted-foreground">Descripción (opcional)</label>
          <Textarea
            value={description} onChange={(e) => setDescription(e.target.value)}
            placeholder="Objetivo del proyecto…" data-testid="create-board-description"
            className="mt-1 bg-white/60 dark:bg-slate-900/40"
          />
        </div>
        <div>
          <label className="text-xs font-medium text-muted-foreground">Color de portada</label>
          <div className="mt-2 grid grid-cols-9 gap-2" data-testid="create-board-color-grid">
            {BOARD_COLORS.map((c) => (
              <button
                type="button" key={c.key} onClick={() => setColor(c.key)}
                data-testid={`create-board-color-${c.key}`}
                title={c.label}
                className={cn(
                  "h-9 rounded-lg bg-gradient-to-br transition-all hover:scale-110 shadow-sm hover:shadow-md",
                  c.gradient,
                  color === c.key && "ring-2 ring-offset-2 ring-offset-transparent ring-blue-500 scale-110"
                )}
                aria-label={c.label}
              />
            ))}
          </div>
        </div>
        <DialogFooter>
          <Button type="button" variant="ghost" onClick={onClose} data-testid="create-board-cancel">Cancelar</Button>
          <Button type="submit" disabled={m.isPending} data-testid="create-board-submit" className="bg-blue-600 hover:bg-blue-700 text-white">
            {m.isPending ? "Creando…" : "Crear board"}
          </Button>
        </DialogFooter>
      </form>
    </>
  );
}

// ---------------------------------------------------------------------------
function TemplatesGallery({ onBack, onPick }) {
  const { data, isLoading } = useQuery({
    queryKey: ["templates"],
    queryFn: async () => (await api.get("/templates")).data,
  });
  const templates = data?.templates || [];

  return (
    <>
      <DialogHeader>
        <button onClick={onBack} className="inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground -mb-1 self-start" data-testid="templates-back">
          <ChevronLeft className="h-4 w-4" /> Atrás
        </button>
        <DialogTitle className="font-display flex items-center gap-2">
          <LayoutTemplate className="h-5 w-5 text-blue-500" />
          Elige una plantilla
        </DialogTitle>
        <DialogDescription>Empieza con un tablero pre-cargado y personalízalo a tu gusto.</DialogDescription>
      </DialogHeader>
      {isLoading ? (
        <p className="text-sm text-muted-foreground py-4">Cargando plantillas…</p>
      ) : (
        <div className="grid sm:grid-cols-2 gap-3 pt-2" data-testid="templates-gallery">
          {templates.map((t) => (
            <button
              key={t.template_id}
              onClick={() => onPick(t.template_id)}
              data-testid={`template-${t.template_id}`}
              className="group text-left rounded-2xl overflow-hidden border border-white/40 dark:border-white/10 bg-white/50 dark:bg-slate-900/40 hover:shadow-[0_18px_40px_-15px_rgba(15,23,42,0.25)] hover:-translate-y-0.5 transition-all"
            >
              <div className={cn("h-20 bg-gradient-to-br relative", gradientFor(t.cover_color))}>
                <div className="absolute top-2 right-2 text-[10px] font-semibold uppercase tracking-wider bg-white/70 dark:bg-slate-900/70 px-2 py-0.5 rounded-full text-slate-800 dark:text-slate-200">
                  {t.category}
                </div>
              </div>
              <div className="p-4">
                <h3 className="font-display font-semibold text-base leading-tight">{t.name}</h3>
                <p className="text-xs text-muted-foreground mt-1 line-clamp-2">{t.description}</p>
                <div className="flex items-center gap-3 mt-2 text-[11px] text-muted-foreground">
                  <span className="inline-flex items-center gap-1"><Layers className="h-3 w-3" /> {t.lists_count} listas</span>
                  <span className="inline-flex items-center gap-1"><ListChecks className="h-3 w-3" /> {t.cards_count} tareas</span>
                </div>
              </div>
            </button>
          ))}
        </div>
      )}
    </>
  );
}

// ---------------------------------------------------------------------------
function TemplatePreview({ templateId, defaultWorkspaceId, onBack, onClose }) {
  const navigate = useNavigate();
  const qc = useQueryClient();
  const [workspaceId, setWorkspaceId] = useState(defaultWorkspaceId || "");
  const [customName, setCustomName] = useState("");

  const { data: template, isLoading } = useQuery({
    queryKey: ["template", templateId],
    queryFn: async () => (await api.get(`/templates/${templateId}`)).data,
    enabled: !!templateId,
  });
  const { data: workspaces = [] } = useQuery({
    queryKey: ["workspaces"],
    queryFn: async () => (await api.get("/workspaces")).data,
  });

  const m = useMutation({
    mutationFn: async () => (
      await api.post("/templates/use", {
        workspace_id: workspaceId || workspaces[0]?.workspace_id,
        template_id: templateId,
        name: customName.trim() || undefined,
      })
    ).data,
    onSuccess: (res) => {
      qc.invalidateQueries({ queryKey: ["recent-boards"] });
      qc.invalidateQueries({ queryKey: ["boards"] });
      toast.success("Plantilla aplicada");
      onClose();
      navigate(`/boards/${res.board_id}`);
    },
    onError: () => toast.error("No se pudo aplicar la plantilla"),
  });

  if (isLoading || !template) {
    return <p className="text-sm text-muted-foreground py-4">Cargando…</p>;
  }

  return (
    <>
      <DialogHeader>
        <button onClick={onBack} className="inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground -mb-1 self-start" data-testid="template-preview-back">
          <ChevronLeft className="h-4 w-4" /> Volver a plantillas
        </button>
        <DialogTitle className="font-display">{template.name}</DialogTitle>
        <DialogDescription>{template.description}</DialogDescription>
      </DialogHeader>

      <div className={cn("h-20 rounded-xl bg-gradient-to-br shadow-lg mt-1", gradientFor(template.cover_color))} />

      <div className="space-y-3 pt-2" data-testid="template-preview-content">
        <div>
          <label className="text-xs font-medium text-muted-foreground">Workspace</label>
          <Select value={workspaceId} onValueChange={setWorkspaceId}>
            <SelectTrigger className="mt-1 bg-white/60 dark:bg-slate-900/40">
              <SelectValue placeholder="Selecciona un workspace" />
            </SelectTrigger>
            <SelectContent>
              {workspaces.map((w) => (
                <SelectItem key={w.workspace_id} value={w.workspace_id}>{w.name}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div>
          <label className="text-xs font-medium text-muted-foreground">Nombre del board (opcional)</label>
          <Input
            value={customName}
            onChange={(e) => setCustomName(e.target.value)}
            placeholder={template.name}
            className="mt-1 bg-white/60 dark:bg-slate-900/40"
            data-testid="template-custom-name"
          />
          <p className="text-[11px] text-muted-foreground mt-1">Si lo dejas vacío usaremos el nombre de la plantilla.</p>
        </div>

        <div className="rounded-xl border border-white/40 dark:border-white/10 bg-white/40 dark:bg-slate-900/30 p-3">
          <p className="text-xs uppercase tracking-wider text-muted-foreground font-semibold mb-2">
            Vista previa · {template.lists?.length || 0} listas · {template.cards?.length || 0} tareas
          </p>
          <div className="flex gap-2 overflow-x-auto kanban-scroll pb-1">
            {(template.lists || []).map((lst, idx) => {
              const listCards = (template.cards || []).filter((c) => c.list_index === idx).slice(0, 4);
              return (
                <div key={idx} className="flex-none w-44 rounded-lg bg-white/70 dark:bg-slate-900/50 border border-white/40 dark:border-white/10 p-2">
                  <p className="text-xs font-semibold mb-1.5 truncate">{lst.name}</p>
                  <div className="space-y-1">
                    {listCards.map((c, ci) => (
                      <div key={ci} className="text-[11px] bg-white dark:bg-slate-800/70 rounded p-1.5 leading-tight line-clamp-2 border border-slate-200/50 dark:border-white/5">
                        {c.title}
                      </div>
                    ))}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>

      <DialogFooter className="pt-2">
        <Button type="button" variant="ghost" onClick={onClose}>Cancelar</Button>
        <Button
          onClick={() => m.mutate()}
          disabled={m.isPending}
          className="bg-blue-600 hover:bg-blue-700 text-white"
          data-testid="template-use-button"
        >
          {m.isPending ? "Creando…" : "Usar esta plantilla"}
        </Button>
      </DialogFooter>
    </>
  );
}
